import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Plus, Trash2, Save, X, Loader2, Star } from "lucide-react";

const EMPTY = { title: "", body: "", isDefault: false };

export default function AdminNoteTemplates() {
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/note-templates");
      setTemplates(r.data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true); setError("");
    try {
      if (editing.id) {
        await api.put(`/note-templates/${editing.id}`, editing);
      } else {
        await api.post("/note-templates", editing);
      }
      setEditing(null);
      await load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Vorlage "${t.title}" löschen?`)) return;
    try {
      await api.delete(`/note-templates/${t.id}`);
      await load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="p-4 md:p-10 max-w-4xl" data-testid="note-templates-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Administration</div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-zinc-900">Hinweise-Vorlagen</h1>
          <p className="text-sm text-zinc-500 mt-1">Erstellen Sie wiederverwendbare Texte für den Hinweise-Abschnitt im PDF.</p>
        </div>
        <button
          onClick={() => setEditing({ ...EMPTY })}
          data-testid="new-template-button"
          className="bg-zinc-900 hover:bg-zinc-800 text-white text-sm font-medium px-4 py-2.5 rounded-sm flex items-center gap-2"
        >
          <Plus size={14}/> Neue Vorlage
        </button>
      </div>

      {error && (
        <div className="text-xs font-medium text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-sm mb-4">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-sm text-zinc-500">Wird geladen...</div>
      ) : templates.length === 0 ? (
        <div className="text-sm text-zinc-500 py-12 text-center border border-dashed border-zinc-200">
          Noch keine Vorlagen. Erstellen Sie die erste, um wiederkehrende Hinweise-Texte zu speichern.
        </div>
      ) : (
        <div className="space-y-3">
          {templates.map((t) => (
            <div key={t.id} className="border border-zinc-200 p-4" data-testid={`template-row-${t.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold text-zinc-900">{t.title}</h3>
                  {t.isDefault && (
                    <span className="text-[10px] uppercase tracking-widest bg-zinc-900 text-white px-1.5 py-0.5 flex items-center gap-1">
                      <Star size={10}/> Standard
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setEditing(t)} data-testid={`template-edit-${t.id}`} className="text-xs text-zinc-600 hover:text-zinc-900 underline">Bearb.</button>
                  <button onClick={() => remove(t)} data-testid={`template-delete-${t.id}`} className="text-zinc-400 hover:text-red-600">
                    <Trash2 size={14}/>
                  </button>
                </div>
              </div>
              <pre className="text-xs text-zinc-600 whitespace-pre-wrap font-sans line-clamp-4">{t.body}</pre>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !saving && setEditing(null)}>
          <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="template-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200">
              <h2 className="text-base font-semibold">{editing.id ? "Vorlage bearbeiten" : "Neue Vorlage"}</h2>
              <button onClick={() => setEditing(null)} className="text-zinc-500 hover:text-zinc-900"><X size={18}/></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1 block">Titel</label>
                <input type="text" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                       data-testid="template-title-input"
                       placeholder="z.B. Standard-Hinweise"
                       className="w-full px-3 py-2 border border-zinc-300 rounded-sm text-sm focus:border-black focus:ring-1 focus:ring-black"/>
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1 block">Text</label>
                <textarea value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                          data-testid="template-body-input"
                          rows={10}
                          placeholder="Mehrzeiliger Hinweistext. Zeilen mit '-' oder '•' werden als Aufzählung dargestellt."
                          className="w-full px-3 py-2 border border-zinc-300 rounded-sm text-sm font-mono focus:border-black focus:ring-1 focus:ring-black"/>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={editing.isDefault || false}
                       onChange={(e) => setEditing({ ...editing, isDefault: e.target.checked })}
                       data-testid="template-default-checkbox"/>
                Als Standard-Vorlage markieren (wird bei neuen Angeboten automatisch geladen)
              </label>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-zinc-200">
              <button onClick={() => setEditing(null)} disabled={saving} className="text-sm px-4 py-2 border border-zinc-300 hover:bg-zinc-50">Abbrechen</button>
              <button onClick={save} disabled={saving || !editing.title.trim()} data-testid="template-save-button"
                      className="bg-zinc-900 hover:bg-zinc-800 text-white text-sm font-medium px-4 py-2 flex items-center gap-2 disabled:opacity-60">
                {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>}
                Speichern
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
